import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, CheckCircle, Clock, AlertCircle, Target, Activity, FileDown, Loader2, Filter, Calendar as CalendarIcon, X } from 'lucide-react';
import usePdfExport from '../hooks/usePdfExport';
import apiService from '../services/api';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-bold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ActivityStats = ({ activities, stats }) => {
  const { exporting, exportPdf, contentRef } = usePdfExport({
    filename: 'activity-report',
    scale: 3,
  });

  // ─── Interactive Filters ───────────────────────────────────
  const [filterBagian, setFilterBagian] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filteredStats, setFilteredStats] = useState(stats);
  const [filterLoading, setFilterLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const uniqueBagians = useMemo(() => ['All', ...new Set(activities.map(a => a.bagian).filter(Boolean))], [activities]);
  const uniqueCategories = useMemo(() => ['All', ...new Set(activities.map(a => a.category).filter(Boolean))], [activities]);

  const applyFilters = useCallback(async () => {
    setFilterLoading(true);
    try {
      const params = {};
      if (filterBagian !== 'All') params.bagian = filterBagian;
      if (filterCategory !== 'All') params.category = filterCategory;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;
      
      const result = await apiService.getStats(params);
      setFilteredStats(result);
    } catch (err) {
      console.error('Failed to fetch filtered stats:', err);
      setFilteredStats(stats);
    } finally {
      setFilterLoading(false);
    }
  }, [filterBagian, filterCategory, filterStartDate, filterEndDate, stats]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const apiStats = filteredStats || stats || {};
  const totalInfo = apiStats.total || {};
  
  const statsData = {
    totalActivities: totalInfo.total || activities.length,
    completedActivities: totalInfo.completed || activities.filter(a => a.status === 'Completed').length,
    inProgressActivities: totalInfo.inProgress || activities.filter(a => a.status === 'In Progress').length,
    pendingActivities: totalInfo.pending || activities.filter(a => a.status === 'Pending').length,
  };

  const completionRate = statsData.totalActivities > 0
    ? Math.round((statsData.completedActivities / statsData.totalActivities) * 100)
    : 0;

  const categoryData = useMemo(() => {
    if (apiStats.categories && apiStats.categories.length > 0) {
      return apiStats.categories.map(cat => ({ name: cat.name, value: cat.count }));
    }
    const categoryCount = {};
    activities.forEach(activity => {
      categoryCount[activity.category] = (categoryCount[activity.category] || 0) + 1;
    });
    return Object.entries(categoryCount).map(([category, count]) => ({
      name: category,
      value: count
    }));
  }, [activities, apiStats.categories]);

  const statusData = useMemo(() => {
    if (apiStats.statuses && apiStats.statuses.length > 0) {
      return apiStats.statuses.map(status => ({ name: status.name, value: status.count }));
    }
    const statusCount = {};
    activities.forEach(activity => {
      statusCount[activity.status] = (statusCount[activity.status] || 0) + 1;
    });
    return Object.entries(statusCount).map(([status, count]) => ({
      name: status,
      value: count
    }));
  }, [activities, apiStats.statuses]);

  const priorityData = useMemo(() => {
    if (apiStats.priorities && apiStats.priorities.length > 0) {
      return apiStats.priorities.map(priority => ({ name: priority.name, value: priority.count }));
    }
    const priorityCount = {};
    activities.forEach(activity => {
      priorityCount[activity.priority] = (priorityCount[activity.priority] || 0) + 1;
    });
    return Object.entries(priorityCount).map(([priority, count]) => ({
      name: priority,
      value: count
    }));
  }, [activities, apiStats.priorities]);

  const bagianData = useMemo(() => {
    if (apiStats.bagianStats && apiStats.bagianStats.length > 0) {
      return apiStats.bagianStats.map(b => ({ name: b.name, value: b.count }));
    }
    const count = {};
    activities.forEach(a => {
      count[a.bagian || 'Umum'] = (count[a.bagian || 'Umum'] || 0) + 1;
    });
    return Object.entries(count).map(([name, value]) => ({ name, value }));
  }, [activities, apiStats.bagianStats]);

  const monthlyData = useMemo(() => {
    if (apiStats.monthly && apiStats.monthly.length > 0) {
      return apiStats.monthly.map(month => ({ 
        month: month.month, 
        activities: month.count 
      }));
    }
    const monthlyCount = {};
    activities.forEach(activity => {
      const month = new Date(activity.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      monthlyCount[month] = (monthlyCount[month] || 0) + 1;
    });
    return Object.entries(monthlyCount).map(([month, count]) => ({
      month,
      activities: count
    })).sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [activities, apiStats.monthly]);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  const STATUS_COLORS = ['#6B7280', '#F59E0B', '#10B981'];
  const PRIORITY_COLORS = ['#3B82F6', '#F59E0B', '#EF4444'];

  const summaryCards = [
    {
      title: 'Total Activities',
      value: statsData.totalActivities,
      icon: Activity,
      gradient: 'from-blue-600 to-blue-400',
      bgLight: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Completed',
      value: statsData.completedActivities,
      icon: CheckCircle,
      gradient: 'from-green-600 to-green-400',
      bgLight: 'bg-green-50',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      badge: `${completionRate}%`,
      badgeColor: 'bg-green-50 text-green-700',
    },
    {
      title: 'In Progress',
      value: statsData.inProgressActivities,
      icon: Clock,
      gradient: 'from-yellow-500 to-orange-400',
      bgLight: 'bg-yellow-50',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
    },
    {
      title: 'Pending',
      value: statsData.pendingActivities,
      icon: AlertCircle,
      gradient: 'from-red-500 to-rose-400',
      bgLight: 'bg-red-50',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" ref={contentRef}>
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Report Activity</h2>
          <p className="mt-1 text-sm text-gray-500">
            Lengkap dan detail — semua filter dapat diaktifkan
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary text-sm ${showFilters || filterBagian !== 'All' || filterCategory !== 'All' || filterStartDate || filterEndDate ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}`}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {(filterBagian !== 'All' || filterCategory !== 'All' || filterStartDate || filterEndDate) && (
              <span className="ml-1.5 w-2 h-2 rounded-full bg-blue-500" />
            )}
          </button>
          <div className="flex items-center space-x-2 bg-blue-50 rounded-lg px-3 py-2">
            <Target className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">
              {completionRate}% completion
            </span>
          </div>
          <button
            onClick={() => exportPdf('Activity-Report')}
            disabled={exporting}
            className="btn-primary text-sm"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            <span>{exporting ? 'Exporting...' : 'Export PDF'}</span>
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      {showFilters && (
        <div className="card animate-slide-up">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Filter Laporan</h3>
              <button
                onClick={() => {
                  setFilterBagian('All');
                  setFilterCategory('All');
                  setFilterStartDate('');
                  setFilterEndDate('');
                }}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                Reset Filters
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Bagian</label>
                <select value={filterBagian} onChange={(e) => setFilterBagian(e.target.value)} className="select-field text-sm">
                  {uniqueBagians.map(b => (
                    <option key={b} value={b}>{b === 'All' ? 'Semua Bagian' : b}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="select-field text-sm">
                  {uniqueCategories.map(c => (
                    <option key={c} value={c}>{c === 'All' ? 'Semua Category' : c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} className="input-field text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} className="input-field text-sm" />
              </div>
            </div>
            {filterLoading && (
              <div className="flex items-center justify-center mt-3">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                <span className="ml-2 text-sm text-gray-500">Memuat data...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {summaryCards.map((card, index) => (
          <div
            key={card.title}
            className="card animate-slide-up overflow-hidden"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-xl ${card.bgLight}`}>
                  <card.icon className={`w-6 h-6 ${card.iconColor}`} />
                </div>
                {card.badge && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${card.badgeColor}`}>
                    {card.badge}
                  </span>
                )}
              </div>
              <div className="mt-4">
                <p className="stat-label">{card.title}</p>
                <p className="stat-value text-gray-900 mt-1">{card.value}</p>
              </div>
              {index === 1 && statsData.totalActivities > 0 && (
                <div className="mt-3">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div
                      className="bg-gradient-to-r from-green-500 to-emerald-400 h-1.5 rounded-full transition-all duration-1000"
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="card animate-slide-up">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <h3 className="section-title">Activities by Category</h3>
            </div>
          </div>
          <div className="card-body">
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%" cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90} innerRadius={30}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-400">No data available</div>
            )}
          </div>
        </div>

        {/* Status Distribution */}
        <div className="card animate-slide-up">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <h3 className="section-title">Activities by Status</h3>
            </div>
          </div>
          <div className="card-body">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={statusData} barSize={60}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                  <YAxis tick={{ fontSize: 13 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-400">No data available</div>
            )}
          </div>
        </div>

        {/* Priority Distribution */}
        <div className="card animate-slide-up">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <h3 className="section-title">Activities by Priority</h3>
            </div>
          </div>
          <div className="card-body">
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={priorityData} barSize={60}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                  <YAxis tick={{ fontSize: 13 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PRIORITY_COLORS[index % PRIORITY_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-400">No data available</div>
            )}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="card animate-slide-up">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <h3 className="section-title">Monthly Trend</h3>
            </div>
          </div>
          <div className="card-body">
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 13 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone" dataKey="activities"
                    stroke="#3B82F6" strokeWidth={3}
                    dot={{ fill: '#3B82F6', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-400">No monthly data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {activities.length === 0 && (
        <div className="card p-12 text-center animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <TrendingUp className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg font-medium">No activities to display</p>
          <p className="text-gray-400 mt-1">Add some activities to see statistics and analytics</p>
        </div>
      )}
    </div>
  );
};

export default ActivityStats;
